import { createContext, useContext, type ComponentType } from 'react';
import type { SessionClaims } from './api.js';

/**
 * WHICH APPLICATION IS RENDERING THIS PAGE.
 *
 * The Hub and the Deal Analyzer are two applications (Brad, 2026-08-26: _"they should be two separate
 * apps"_) that share four pages — `ProjectsList`, `ProjectView`, `NewProject`, `StandardsQueue` are
 * reachable in both. Those pages used to import `AppHeader` directly, which meant the Hub wore the
 * ANALYZER's chrome: its logo lockup, its nav, and two destinations that do not exist in the Hub's
 * router. `Deals` navigated to `/analyses` and `+ New deal` to `/`, and both fell through the Hub's
 * catch-all straight back to `/projects` — a primary action, in the header, on every page, that
 * silently returned you to where you already were.
 *
 * So the header is supplied BY THE APPLICATION ROOT rather than imported by the page. `App.tsx`
 * provides `AppHeader`; `HubApp.tsx` provides `HubHeader`. Neither header is reachable from the other
 * app's entry point, which is the separation being asked for — the shared pages no longer decide what
 * product they are part of.
 *
 * ⚠️ The default is `null`, deliberately: there is no sensible "probably the analyzer" fallback, and a
 * page silently wearing the wrong app's identity is the defect this exists to remove. A root that
 * forgets the provider renders no header at all, which is loud — and `chrome.guard.test.ts` fails
 * before it can ship.
 */
export type ChromeHeader = ComponentType<{ claims: SessionClaims }>;

const ChromeContext = createContext<ChromeHeader | null>(null);

/** Supplied once, at each application root, around that app's routes. */
export const ChromeProvider = ChromeContext.Provider;

/** The header this application wants. `null` only if a root forgot to provide one. */
export function useChrome(): ChromeHeader | null {
  return useContext(ChromeContext);
}

/**
 * WHICH SURFACE a shared page is on — for the rare feature that belongs to ONE application.
 *
 * The first is ask-the-record (ADR-0045 §4, ruled by Brad 2026-09-10: "Hub only"). `ProjectView` is
 * rendered by both roots, so without this the staff answerer also appeared on `app.*` — the analyzer
 * surface a client will look at.
 *
 * ⚠️ Kept SEPARATE from `ChromeProvider` rather than folded into its value: `chrome.guard.test.ts`
 * pins the exact `<ChromeProvider value={…}>` strings, and a header and a surface are different facts.
 *
 * ⚠️ The default is `null`, and `null` means "NOT the Hub" — a root that forgets the provider hides a
 * Hub-only feature rather than leaking it onto the other surface. Fail closed, like `roles.ts`.
 */
export type AppSurface = 'hub' | 'analyzer';

const SurfaceContext = createContext<AppSurface | null>(null);

/** Supplied once, at each application root, beside `ChromeProvider`. */
export const SurfaceProvider = SurfaceContext.Provider;

/** The surface this page is rendered on. `null` if a root forgot to say — treat it as not the Hub. */
export function useAppSurface(): AppSurface | null {
  return useContext(SurfaceContext);
}
